import {Injectable} from '@angular/core';
import {ToastContainerDirective, ToastrService} from 'ngx-toastr';
import {ActiveToast} from 'ngx-toastr/toastr/toastr.service';
import {
  MIN_TOAST_SHOWING_TIME,
  OPENED_TOASTS_LIMIT,
  TOAST_MESSAGE_TIMEOUT,
  ToastMessage,
  ToastMessageType,
} from '../types/toastr.type';

@Injectable({providedIn: 'root'})
export class ToastrMessagesProvider {
  private mainToastrContainer: ToastContainerDirective;
  private drawerToastrContainer: ToastContainerDirective;
  // Store own messageQueue because toastrService doesn't provide full info for active toasters
  private messageQueue: ToastMessage[] = [];

  constructor(private toastrService: ToastrService) {}

  get hasDrawerContainer() {
    return !!this.drawerToastrContainer;
  }

  get mainContainer() {
    return this.mainToastrContainer;
  }

  set commonMessageContainer(value: ToastContainerDirective) {
    this.mainToastrContainer = value;
    this.setNewContainer(value);
  }

  set drawerMessageContainer(value: ToastContainerDirective) {
    this.drawerToastrContainer = value;
    this.setNewContainer(value);
  }

  success(message: string): ActiveToast<void> {
    return this.showToast(ToastMessageType.Success, message);
  }

  warning(message: string, isHtmlMessage: boolean = false): ActiveToast<void> {
    return this.showToast(ToastMessageType.Warning, message, isHtmlMessage);
  }

  switchToMainContainer() {
    this.setNewContainer(this.mainToastrContainer);
  }

  private showToast(type: ToastMessageType, message: string, isHtmlMessage: boolean = false): ActiveToast<void> {
    this.checkQueueLimits();
    const toast: ActiveToast<void> = this.toastrService[type](message, null, {enableHtml: isHtmlMessage});
    const timestamp = Date.now();
    const {toastId} = toast;
    const toastMessage: ToastMessage = {
      toastId,
      message,
      type,
      timestamp,
      timeout: this.toastRemoveTimeout(toastId),
      isHtmlMessage,
    };
    this.messageQueue.push(toastMessage);
    return toast;
  }

  private toastRemoveTimeout(id: number): ReturnType<typeof setTimeout> {
    return setTimeout(() => {
      this.messageQueue = this.messageQueue.filter(msg => msg.toastId !== id);
    }, TOAST_MESSAGE_TIMEOUT);
  }

  private checkQueueLimits() {
    if (this.messageQueue.length === OPENED_TOASTS_LIMIT) {
      this.messageQueue.shift();
    }
  }

  private clear() {
    this.toastrService.clear();
    this.messageQueue.forEach(({timeout}) => clearTimeout(timeout));
  }

  private setNewContainer(container: ToastContainerDirective) {
    if (!container) {
      console.warn('Toast message container should not be empty');
      return;
    }

    // do not duplicate toast if the same container passed
    if (container.getContainerElement().id === this.toastrService.overlayContainer?.getContainerElement()?.id) {
      return;
    }

    // if toast was shown less than MIN_TOAST_SHOWING_TIME - show it again in new container
    const messages = [...this.messageQueue.filter(toast => Date.now() - toast.timestamp < MIN_TOAST_SHOWING_TIME)];
    this.toastrService.overlayContainer = container;
    this.clear();

    // on container change - show current toasters in new container
    messages.forEach(({type, message, isHtmlMessage}) => {
      switch (type) {
        case ToastMessageType.Success:
          this.success(message);
          break;
        case ToastMessageType.Warning:
          this.warning(message, isHtmlMessage);
          break;
      }
    });
  }
}
